/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import { Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	CONFLUENCE_CONSUMER_PROMPTS,
	CONSUMER_PROMPTS,
	CREATOR_PROMPTS,
	type PromptStubType,
} from './constants';
import messages, { ALL_PROMPTS } from './messages';

const styles = cssMap({
	container: {
		width: '100%',
		height: '100%',
		display: 'flex',
		flexGrow: 1,
		flexDirection: 'column',
		paddingBottom: token('space.150'),
		justifyContent: 'space-between',
	},
	wrapper: {
		width: '100%',
		height: '100%',
		flexGrow: 1,
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingLeft: token('space.300'),
		paddingBottom: token('space.1000'),
	},
	promptsContainer: {
		borderRadius: token('radius.xlarge'),
		marginInline: token('space.200'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'flex-end',
		textAlign: 'left',
		backgroundColor: token('color.background.input'),
		boxShadow: token('elevation.shadow.overflow'),
	},
	promptButtonStyles: {
		width: '100%',
		height: '40px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		gap: token('space.100'),
		textAlign: 'left',
		borderRadius: token('radius.medium'),
		backgroundColor: token('color.background.input'),
		color: token('color.text'),
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
});

export type UserPersonaType = 'creator' | 'consumer';
export type ProductType = 'jira' | 'confluence';

const getPrompts = (userPersona: UserPersonaType, product: ProductType): PromptStubType[] => {
	if (product === 'confluence') {
		return CONFLUENCE_CONSUMER_PROMPTS;
	}
	return userPersona === 'creator' ? CREATOR_PROMPTS : CONSUMER_PROMPTS;
};

export const ChatOnboardingPrompts = ({
	userPersona,
	onNext,
	onStepViewed,
	product,
}: {
	userPersona: UserPersonaType;
	onNext: (buttonName: string, additionalAttributes: Record<string, string>) => void;
	onStepViewed: (additionalAttributes?: Record<string, string>) => void;
	product: ProductType;
}): JSX.Element => {
	const { formatMessage } = useIntl();
	const hasClickedRef = useRef(false);
	const prompts = getPrompts(userPersona, product);

	useEffect(() => {
		onStepViewed();
	}, [onStepViewed]);

	const handleOnClick = (promptId: string) => {
		if (!hasClickedRef.current) {
			hasClickedRef.current = true;
			const promptMessage = formatMessage(ALL_PROMPTS[promptId as keyof typeof ALL_PROMPTS]);
			onNext('prompt', {
				promptId,
				promptMessage,
			});
		}
	};

	return (
		<Stack xcss={styles.container}>
			<Stack xcss={styles.wrapper} space="space.200">
				<Heading size="small">
					<FormattedMessage {...messages.headingForPrompts} />
				</Heading>
				<Text size="medium" as="p">
					<FormattedMessage {...messages.descriptionForPrompts} />
				</Text>
			</Stack>
			<Stack xcss={styles.promptsContainer} testId="chat-onboarding-prompts-container">
				{prompts.map((prompt, index) => (
					<Pressable
						key={index}
						onClick={() => handleOnClick(prompt.promptId)}
						xcss={styles.promptButtonStyles}
					>
						<Image src={prompt.iconLight} srcDark={prompt.iconDark} alt="" width={24} height={24} />
						<FormattedMessage {...prompt.message} />
					</Pressable>
				))}
			</Stack>
		</Stack>
	);
};
